import React, { useState, useEffect, useRef } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { ArrowUpDown, Grid3x3, List, Search } from "lucide-react";
import { useDispatch } from "react-redux";
import { openSearchModal } from "@/modules/hub/features/Search/slices/searchSlice";

import { ResourceCardV2 } from "@/modules/hub/features/ResourceLibraryV2/components/ResourceCardV2/ResourceCardV2";
import { ResourceListItemV2 } from "@/modules/hub/features/ResourceLibraryV2/components/ResourceListItemV2/ResourceListItemV2";
import { FilterSidebar } from "@/modules/hub/features/ResourceLibraryV2/components/FilterSidebar/FilterSidebar/FilterSidebar";
import { FeaturedCarouselV2 } from "@/modules/hub/features/ResourceLibraryV2/components/FeaturedCarouselV2/FeaturedCarouselV2";
import ActiveFiltersHandler from "@/modules/hub/features/ResourceLibraryV2/components/ActiveFiltersHandler/ActiveFiltersHandler";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import "./ResourceLibraryV2.css";
import { useResourcesLibrary } from "../../hooks/useResourceLibrary";
import { useIsMobile } from "@/hooks/use-mobile";
import MobileFilterToggleButton from "../MobileFilterToggleButton/MobileFilterToggleButton";
import { ResourcesLibraryV2Skeleton } from "./ResourceLibraryV2Skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { trackResourceLibraryPageViewed } from "../../analytics/resourceLibraryAnalytics";

type SortOption = "newest" | "oldest" | "mostViewed" | "alphabetical";
type ViewMode = "card" | "list";

export function ResourcesLibraryV2() {
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (!hasTrackedPageView.current) {
      trackResourceLibraryPageViewed();
      hasTrackedPageView.current = true;
    }
  }, []);

  const dispatch = useDispatch();
  const {
    form,
    resources,
    hasMore,
    totalCount,
    loading,
    isInitialLoading,
    sidebarMetadata,
    isMetadataLoading,
    fetchMore,
    handleClearFilters,
    sortOption,
    setSortOption,
    handleFavoriteToggle,
  } = useResourcesLibrary("library-v2");

  const isMobile = useIsMobile();
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>("card");

  // Load view preference from localStorage
  useEffect(() => {
    const savedView = localStorage.getItem("resourceLibraryView") as ViewMode;
    if (savedView === "card" || savedView === "list") {
      setViewMode(savedView);
    }
  }, []);

  // Save view preference to localStorage
  useEffect(() => {
    localStorage.setItem("resourceLibraryView", viewMode);
  }, [viewMode]);

  const sortOptions: { value: SortOption; label: string }[] = [
    { value: "newest", label: "Newest First" },
    { value: "oldest", label: "Oldest First" },
    { value: "mostViewed", label: "Most Viewed" },
    { value: "alphabetical", label: "Title A-Z" },
  ];

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
  };

  if (isInitialLoading) {
    return <ResourcesLibraryV2Skeleton />;
  }

  return (
    <MainPageWrapper
      actions={
        <MobileFilterToggleButton
          onClick={() => setIsFilterSidebarOpen(true)}
        />
      }
      fullWidth
      sidebar={
        <div
          className={
            isMobile
              ? undefined
              : "w-80 shrink-0 self-start bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 p-2 sticky top-12 h-[calc(100vh-11rem)] overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-sm dark:[&::-webkit-scrollbar-thumb]:bg-gray-600"
          }
        >
          <FilterSidebar
            filters={sidebarMetadata}
            form={form}
            isLoading={isMetadataLoading}
            isOpen={isFilterSidebarOpen}
            onOpenChange={setIsFilterSidebarOpen}
            showJobTitle={false}
            showProcessWheel
          />
        </div>
      }
      subtitleSecondLine="Browse by process business area, search by keyword, or refine with filters."
      title="Resource Library"
    >
      <div className="flex-1 min-w-0">
        {/* Featured Resources Section - Only show when no active filters */}
        {!form.watch("isFavoriteOnly") &&
          !form.watch("isFeaturedOnly") &&
          form.watch("trades").length === 0 &&
          form.watch("contentTypes").length === 0 &&
          form.watch("categories").length === 0 &&
          form.watch("businessRoles").length === 0 && (
            <FeaturedCarouselV2 context="library-v2" />
          )}

        <ActiveFiltersHandler form={form} onClearAll={handleClearFilters} />

        {/* Sort Controls and View Toggle */}
        <div className="flex mb-4 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl px-3 py-1.5 flex-col items-start gap-2 lg:flex-row lg:items-center lg:justify-between lg:gap-0">
          <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
            {!isInitialLoading && `${totalCount} resources found`}
          </div>
          <div className="flex items-center gap-3 w-full justify-between lg:w-auto lg:justify-end">
            {/* Sort Dropdown */}
            <div className="flex items-center gap-2 flex-1 min-w-0 lg:flex-none">
              <ArrowUpDown className="h-4 w-4 text-gray-500 shrink-0" />
              <Select
                onValueChange={(value) => setSortOption(value as SortOption)}
                value={sortOption}
              >
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder="Sort by..." />
                </SelectTrigger>
                <SelectContent>
                  {sortOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-1 border border-gray-200 dark:border-gray-700 rounded-md p-1">
              <Button
                className={`h-7 w-7 p-0 ${
                  viewMode === "card"
                    ? "bg-red-700 text-white hover:bg-red-800 hover:text-white"
                    : ""
                }`}
                onClick={() => handleViewModeChange("card")}
                size="sm"
                title="Card View"
                variant="ghost"
              >
                <Grid3x3 className="h-4 w-4" />
              </Button>
              <Button
                className={`h-7 w-7 p-0 ${
                  viewMode === "list"
                    ? "bg-red-700 text-white hover:bg-red-800 hover:text-white"
                    : ""
                }`}
                onClick={() => handleViewModeChange("list")}
                size="sm"
                title="List View"
                variant="ghost"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        <InfiniteScroll
          dataLength={resources.length}
          endMessage={
            resources.length > 0 ? (
              <div className="text-center py-4">
                <p className="text-gray-500 dark:text-gray-400">
                  No more resources to load
                </p>
              </div>
            ) : null
          }
          hasMore={hasMore}
          loader={
            <div className="text-center py-4">
              <p className="text-gray-500 dark:text-gray-400">
                Loading more...
              </p>
            </div>
          }
          next={fetchMore}
          scrollableTarget="main-scroll-area"
        >
          {loading && resources.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg">
                Searching...
              </p>
            </div>
          ) : resources.length > 0 ? (
            viewMode === "card" ? (
              <div className="grid gap-6 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {resources.map((resource) => (
                  <div className="fade-in" key={resource.id}>
                    <ResourceCardV2
                      onToggleFavorite={handleFavoriteToggle}
                      resource={resource}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {resources.map((resource) => (
                  <div className="fade-in" key={resource.id}>
                    <ResourceListItemV2
                      onToggleFavorite={handleFavoriteToggle}
                      resource={resource}
                    />
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500 dark:text-gray-400 text-lg mb-4">
                No resources found matching your criteria.
              </p>

              {/* Search prompt if no filters are active */}
              {!form.watch("isFavoriteOnly") &&
                !form.watch("isFeaturedOnly") &&
                form.watch("trades").length === 0 &&
                form.watch("contentTypes").length === 0 &&
                form.watch("categories").length === 0 &&
                form.watch("businessRoles").length === 0 && (
                  <div className="mt-8 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg max-w-md mx-auto">
                    <Search className="h-8 w-8 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                    <p className="text-gray-600 dark:text-gray-300 font-medium mb-2">
                      Looking for something specific?
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                      Search across all resources and events
                    </p>
                    <button
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                      onClick={() => dispatch(openSearchModal())}
                    >
                      <Search className="h-4 w-4" />
                      Open Search
                      <kbd className="ml-2 px-1.5 py-0.5 text-xs bg-blue-700 rounded">
                        {navigator.platform?.includes("Mac") ? "⌘K" : "Ctrl+K"}
                      </kbd>
                    </button>
                  </div>
                )}

              {/* Clear filters button if filters are active */}
              {(form.watch("isFavoriteOnly") ||
                form.watch("isFeaturedOnly") ||
                form.watch("trades").length > 0 ||
                form.watch("contentTypes").length > 0 ||
                form.watch("categories").length > 0 ||
                form.watch("businessRoles").length > 0) && (
                <button
                  className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
                  onClick={handleClearFilters}
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}
        </InfiniteScroll>
      </div>
    </MainPageWrapper>
  );
}
